import { STRIPE_PRODUCTS } from '../stripe-config';
import { supabase } from './supabase';

export async function createCheckoutSession(
  priceId: string,
  mode: 'subscription' | 'payment',
  successUrl: string,
  cancelUrl: string,
) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      throw new Error('Please sign in to continue with checkout');
    }

    // Get the current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) {
      throw new Error('Unable to verify user authentication');
    }

    // Check if customer already exists via profiles table
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('customer_id')
      .eq('id', user.id)
      .maybeSingle();

    if (profileError) {
      console.error('Error checking customer:', profileError);
    }
    const customerData = profileData;

    const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-checkout`;

    // `trialDays` is not a field on any product in STRIPE_PRODUCTS and never
    // was, so this always resolved to undefined and no trial was ever sent.
    // That is why "$1 your first month" was advertised while checkout charged
    // the full price. The copy is gone; this is the dead code behind it.

    console.log('Creating checkout session:', {
      priceId,
      mode,
      successUrl,
      cancelUrl,
      functionUrl,
      customerId: customerData?.customer_id,
    });

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        price_id: priceId,
        mode,
        success_url: successUrl,
        cancel_url: cancelUrl,
        customer_id: customerData?.customer_id,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: 'Unable to connect to payment service' };
      }

      let errorMessage = 'Unable to process your payment request';
      if (response.status === 401) {
        errorMessage = 'Please sign in to continue with checkout';
      } else if (response.status === 400) {
        errorMessage = errorData.error || 'Invalid payment request';
      } else if (response.status === 503) {
        errorMessage = 'Payment service is temporarily unavailable. Please try again later';
      } else if (!response.ok) {
        errorMessage = 'Network error. Please check your internet connection and try again';
      }

      console.error('Checkout session creation failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorData
      });
      
      throw new Error(errorMessage);
    }

    const data = await response.json();
    
    if (!data.url) {
      throw new Error('Unable to create checkout session. Please try again');
    }

    return { sessionId: data.sessionId, url: data.url };
  } catch (error: any) {
    console.error('Error creating checkout session:', {
      message: error.message,
      stack: error.stack,
      cause: error.cause
    });
    throw new Error(error.message || 'Unable to process checkout request');
  }
}

export async function redirectToCheckout(product: typeof STRIPE_PRODUCTS[keyof typeof STRIPE_PRODUCTS]) {
  try {
    if (!product?.priceId) {
      throw new Error('Invalid product configuration');
    }

    const { url } = await createCheckoutSession(
      product.priceId,
      product.mode,
      `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
      `${window.location.origin}/checkout/cancel`
    );

    if (url) {
      window.location.href = url;
    } else {
      throw new Error('Unable to start checkout process');
    }
  } catch (error: any) {
    console.error('Error redirecting to checkout:', {
      message: error.message,
      stack: error.stack,
      cause: error.cause
    });
    throw error;
  }
}

/**
 * Open the Stripe Customer Billing Portal so the user can manage, update the
 * payment method for, or CANCEL their web (Stripe) subscription.
 *
 * Web subscribers — including iOS users who subscribed on the website — cannot
 * cancel via Apple Settings because the charge is on Stripe, not Apple IAP.
 * This is the correct cancellation path for them.
 */
export async function openBillingPortal(returnUrl: string = `${window.location.origin}/billing`) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    throw new Error('Please sign in to manage your subscription.');
  }

  const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-billing-portal`;
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ return_url: returnUrl }),
  });

  const data = await response.json().catch(() => ({} as any));

  if (!response.ok || !data?.url) {
    if (data?.error === 'no_stripe_customer') {
      throw new Error(
        "We couldn't find a web subscription for this account. If you subscribed inside the iPhone app, cancel from iPhone Settings → tap your name → Subscriptions. If you subscribed on Android, cancel from the Google Play Store → Subscriptions.",
      );
    }
    throw new Error(data?.error || 'Unable to open the billing portal. Please try again or email support@carsjudge.ai.');
  }

  window.location.href = data.url;
}

export interface SubscriptionStatus {
  premium_status: boolean;
  subscription_status: string;
  current_period_end: number | null;
  product_name: string | null;
  price_id: string | null;
  customer_id: string | null;
  subscription_id: string | null;
  cancel_at_period_end: boolean;
  status: string; // alias for subscription_status for backward compat
}

export async function getSubscriptionStatus(): Promise<SubscriptionStatus | null> {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError || !session) return null;

    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError || !user) return null;

    // Read from profiles table — same source of truth as mobile app
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('premium_status, subscription_status, current_period_end, product_name, price_id, customer_id, subscription_id, cancel_at_period_end')
      .eq('id', user.id)
      .maybeSingle();

    if (profileError) {
      console.error('Error fetching profile subscription:', profileError);
      return null;
    }

    if (!profile) {
      return {
        premium_status: false,
        subscription_status: 'inactive',
        current_period_end: null,
        product_name: null,
        price_id: null,
        customer_id: null,
        subscription_id: null,
        cancel_at_period_end: false,
        status: 'inactive',
      };
    }

    // Parse current_period_end — could be ISO string or timestamp
    let currentPeriodEnd: number | null = null;
    if (profile.current_period_end) {
      if (typeof profile.current_period_end === 'string') {
        currentPeriodEnd = Math.floor(new Date(profile.current_period_end).getTime() / 1000);
      } else {
        currentPeriodEnd = profile.current_period_end;
      }
    }

    const isPremium = profile.premium_status === true || profile.premium_status === 'true';
    const subStatus = profile.subscription_status || 'inactive';

    return {
      premium_status: isPremium,
      subscription_status: subStatus,
      current_period_end: currentPeriodEnd,
      product_name: profile.product_name || null,
      price_id: profile.price_id || null,
      customer_id: profile.customer_id || null,
      subscription_id: profile.subscription_id || null,
      cancel_at_period_end: profile.cancel_at_period_end || false,
      status: subStatus, // backward compat alias
    };
  } catch (error) {
    console.error('Error in getSubscriptionStatus:', error);
    return null;
  }
}

/**
 * Check if subscription grants premium access (matches mobile app logic)
 */
export function hasActiveSubscription(sub: SubscriptionStatus | null): boolean {
  if (!sub) return false;
  const now = Math.floor(Date.now() / 1000);

  // Premium flag is set and period hasn't expired
  if (sub.premium_status === true) {
    if (!sub.current_period_end) return true;
    if (sub.current_period_end > now) return true;
  }

  // Trialing gets full access
  if (sub.subscription_status === 'trialing') {
    if (!sub.current_period_end || sub.current_period_end > now) return true;
  }

  // Canceled but still within paid period
  if (sub.current_period_end && sub.current_period_end > now) {
    if (sub.subscription_status === 'canceled' || sub.cancel_at_period_end) return true;
  }

  return false;
}